import { useEffect, useMemo } from "react";
import { ArrowLeft, Home } from "lucide-react";
import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

const STACK_KEY = "inquira_nav_stack";

/**
 * Routes we never send a user "Back" into: auth screens, OAuth/affiliate
 * redirect hops, checkout returns and one-off debug links. Landing on them a
 * second time either loops or re-triggers a side effect.
 */
const UNSAFE_BACK_PREFIXES = [
  "/login",
  "/signup",
  "/reset-password",
  "/checkout/return",
  "/affiliate/",
  "/debug-report",
];

function isUnsafeBack(path: string) {
  return UNSAFE_BACK_PREFIXES.some((p) => path === p || path.startsWith(p));
}

function readStack(): string[] {
  try {
    const raw = sessionStorage.getItem(STACK_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeStack(stack: string[]) {
  try {
    sessionStorage.setItem(STACK_KEY, JSON.stringify(stack.slice(-25)));
  } catch {
    /* ignore */
  }
}

/**
 * Global Back / Home controls. Rendered once from the root route so every
 * user-facing page gets them without duplicating navigation systems.
 */
export function AppNavigationControls() {
  const router = useRouter();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Track internal navigation so Back can fall back safely on direct links.
  useEffect(() => {
    const stack = readStack();
    if (stack[stack.length - 1] !== pathname) writeStack([...stack, pathname]);
  }, [pathname]);

  const isHome = pathname === "/";

  /**
   * Landing on an auth screen almost always means a guarded route bounced us
   * here. Stepping back would re-enter that route and bounce again, so auth
   * screens always fall back Home.
   */
  const isAuthScreen = isUnsafeBack(pathname);

  const previous = useMemo(() => {
    if (isAuthScreen) return null;
    const stack = readStack();
    for (let i = stack.length - 2; i >= 0; i--) {
      const candidate = stack[i];
      if (candidate && candidate !== pathname && !isUnsafeBack(candidate)) return candidate;
    }
    return null;
  }, [pathname, isAuthScreen]);

  if (isHome) return null;

  const goBack = () => {
    if (previous) {
      // Prefer real history so scroll restoration works, but only when the
      // previous entry is genuinely the one behind us in this app session.
      const stack = readStack();
      if (stack.length > 1 && stack[stack.length - 2] === previous) {
        router.history.back();
        return;
      }
      void navigate({ to: previous });
      return;
    }
    void navigate({ to: "/" });
  };


  return (
    <nav
      aria-label={t("nav.pageNavigation", "Page navigation")}
      className="w-full px-3 pt-2 pb-1"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t("common.back")}
          className="inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-full border border-rule bg-card px-3 text-sm font-semibold text-ink transition-colors hover:bg-rule/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft className="size-4 shrink-0 rtl:rotate-180" aria-hidden="true" />
          <span className="truncate">{t("common.back")}</span>
        </button>
        <button
          type="button"
          onClick={() => void navigate({ to: "/" })}
          aria-label={t("nav.home")}
          className="inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-full border border-rule bg-card px-3 text-sm font-semibold text-ink transition-colors hover:bg-rule/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Home className="size-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{t("nav.home")}</span>
        </button>
      </div>
    </nav>
  );
}
